import { useEffect, useState } from 'react';
import { getCopy } from '../utils/copy';

/**
 * 分类归类关(9-12 岁维度用,如"把省份分到省/自治区/直辖市/特别行政区")
 *
 * 交互沿用 SpatialPuzzle3D 已验证的"先选中、再点目标"两次点击模式,不用拖拽:
 * 先点一个未分类的 item 选中(高亮),再点一个 category 桶把它放进去;
 * 已分类的 item 再点一次可以取消、退回待分类区(允许反悔,呼应无挫败原则)。
 * 全部 item 都有归属后自动提交 —— answer 是 { itemId: categoryId, ... } 的
 * 扁平映射,和 solution 同构,复用现有 `answer === puzzle.solution` 的直接
 * 比较协议,不需要新的比较逻辑。
 */
function CategorySortPuzzle({ puzzle, onComplete, feedback, ageBand }) {
  const { prompt, categories, items } = puzzle.params;
  const [assignments, setAssignments] = useState({});
  const [selectedItemId, setSelectedItemId] = useState(null);

  // 只有每张卡都配了 emoji 才显示:只给一部分卡配图,看着不齐,还像在暗示答案
  const showEmoji = items.every((item) => item.emoji);
  const unassignedItems = items.filter((item) => !assignments[item.id]);
  const allAssigned = items.every((item) => assignments[item.id]);

  useEffect(() => {
    if (allAssigned) {
      const timer = setTimeout(() => onComplete(assignments), 500);
      return () => clearTimeout(timer);
    }
  }, [allAssigned, assignments, onComplete]);

  const handleItemClick = (itemId) => {
    if (assignments[itemId]) {
      // 已分类的 item 再点一次 = 取消分类,退回待分类区
      setAssignments((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      return;
    }
    setSelectedItemId((prev) => (prev === itemId ? null : itemId));
  };

  const handleCategoryClick = (categoryId) => {
    if (!selectedItemId) return;
    setAssignments((prev) => ({ ...prev, [selectedItemId]: categoryId }));
    setSelectedItemId(null);
  };

  return (
    <div className="flex w-full flex-col items-center gap-6 rounded-2xl border-2 border-black bg-white p-4 shadow-brutal sm:p-8">
      <p className="text-lg font-bold text-gray-800">{puzzle.title}</p>
      {prompt && <p className="break-words text-center text-lg font-medium text-gray-700">{prompt}</p>}
      {feedback && !feedback.correct && (
        <p role="alert" className="text-center text-base font-medium text-orange-700">
          {getCopy(ageBand).retryHint}
        </p>
      )}

      <div className="flex w-full flex-wrap justify-center gap-3">
        {unassignedItems.map((item) => (
          <button
            key={item.id}
            type="button"
            data-testid={`item-${item.id}`}
            onClick={() => handleItemClick(item.id)}
            className={`flex items-center gap-1 rounded-xl border-2 border-black px-3 py-2
                        min-h-[44px] text-base font-bold shadow-brutal-sm transition
                        hover:-translate-y-0.5 active:translate-y-0 active:shadow-none
                        ${selectedItemId === item.id ? 'bg-qiji-mint' : 'bg-amber-50'}`}
          >
            {showEmoji && <span className="text-xl">{item.emoji}</span>}
            <span>{item.label}</span>
          </button>
        ))}
      </div>

      <div className="grid w-full gap-3" style={{ gridTemplateColumns: `repeat(${categories.length}, minmax(0, 1fr))` }}>
        {categories.map((category) => {
          const categoryItems = items.filter((item) => assignments[item.id] === category.id);
          return (
            <div
              key={category.id}
              className="flex min-h-[6rem] flex-col items-center gap-2 rounded-xl border-2
                         border-dashed border-gray-400 bg-amber-50/50 p-3"
            >
              {/* 类别的点击目标固定是这颗小按钮,不是整个桶——桶里的物品会
                  越堆越多,如果点击目标是整个桶,后续点击很容易落在已有的
                  物品卡片上(卡片会拦截冒泡,导致"放进去"这个动作静默失效,
                  孩子点了却没反应)。固定住一个不随内容变化的点击区域,
                  才能保证不管桶里已经有多少物品,"点这里放进去"永远有效。 */}
              <button
                type="button"
                data-testid={`category-${category.id}`}
                onClick={() => handleCategoryClick(category.id)}
                className="min-h-[44px] break-words rounded-lg px-2 py-1 text-base font-bold text-gray-700 transition
                           hover:bg-amber-100 active:bg-amber-200"
              >
                {category.label}
              </button>
              <div className="flex flex-wrap justify-center gap-2">
                {categoryItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`item-${item.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(item.id);
                    }}
                    className="flex items-center gap-1 rounded-lg border-2 border-black
                               min-h-[36px] bg-white px-2 py-1 text-sm font-bold shadow-brutal-sm"
                  >
                    {showEmoji && <span>{item.emoji}</span>}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-sm text-gray-600">
        先点一个还没分类的卡片选中,再点目标类别把它放进去吧!
      </p>
    </div>
  );
}

export default CategorySortPuzzle;
